import type { ApiGem, ApiLayoutEntry, ApiRingTemplate, ApiSettingType } from '../../types/api';

/**
 * Mock dataset for backend-less / firewalled deployments of hd_neo.
 *
 * This file mirrors the shape of `RING_TEMPLATES_BY_GROUP_AND_CONFIGURATOR_QUERY`
 * response and is consumed by `mockLink` (see ./mockLink.ts) when
 * `VITE_USE_MOCK_API=true`.
 *
 * DXF files referenced via `ringProfile.dxfFiles[].file.path` must exist under
 *   public/uploads/<path>
 * so both the Vite dev server and the production Caddy image serve them at
 *   /uploads/<path>
 * (matching the live ERP URL scheme — see ExtrudedRingMesh.tsx).
 *
 * To swap in your own profiles:
 *   1. Drop the .dxf files into public/uploads/dxf/
 *   2. Update the `path` strings below to match the filenames
 */

const yellowGold = {
    id: 'mock-color-yellow',
    short: 'YG',
    nameDe: 'Gelbgold',
    nameEn: 'Yellow gold',
    hexColorValue: 'd4af6a',
};

const whiteGold = {
    id: 'mock-color-white',
    short: 'WG',
    nameDe: 'Weißgold',
    nameEn: 'White gold',
    hexColorValue: 'd8dadc',
};

const polishedSurface = {
    id: 'mock-surface-polished',
    short: 'POL',
    nameDe: 'Poliert',
    nameEn: 'Polished',
    averageRoughnessValue: 0.05,
    roughnessFile: null,
    normalFile: null,
};

const makeAlloy = (
    id: string,
    short: string,
    nameDe: string,
    color: typeof yellowGold,
    sortingIndex: number,
) => ({
    id,
    short,
    nameDe,
    nameEn: nameDe,
    sortingIndex,
    color,
});

const makeProfile = (
    id: string,
    name: string,
    labelDe: string,
    dxfPath: string,
    formFactor: number,
) => ({
    id,
    name,
    short: id,
    labelDe,
    labelEn: labelDe,
    formFactor,
    dxfFiles: [
        {
            id: `${id}-dxf`,
            file: { id: `${id}-dxf-file`, path: dxfPath },
        },
    ],
    dxfLoopFiles: [],
});

const baseRingPartial = {
    index: 0,
    ringSegments: [],
    gemClasses: [],
    surfaces: [polishedSurface],
};

// ─── Gem data ──────────────────────────────────────────────────────────────────

const brilliantCut = {
    id: 'mock-cut-brilliant',
    short: 'BRL',
    nameDe: 'Brillant',
    nameEn: 'Brilliant',
    shape: 'ROUND',
};

/** 1.5 mm round brilliant — the smallest common flush-set stone */
const gem1_5: ApiGem = {
    id: 'mock-gem-br-1_5',
    short: 'BR015',
    nameDe: 'Brillant 1,5 mm',
    nameEn: 'Brilliant 1.5 mm',
    carat: 0.015,
    diameter: 1.5,
    width: 1.5,
    length: 1.5,
    height: 0.9,
    cut: brilliantCut,
};

// ─── Starfield layout helpers ──────────────────────────────────────────────────
//
// STARFIELD placement maths (for ring size 54, height 2 mm):
//   innerRadius  = 54 / (2π)         ≈  8.594 mm
//   outerRadius  = innerRadius + 2    ≈ 10.594 mm
//   scene scale  = 0.2 su/mm
//   outer circumference (scene)       ≈ 2π × 2.119 su  ≈ 13.31 su
//
// offsetX (mm) → t via:  t = mmToScene(|offsetX|) / curveLength
//                           = offsetX × 0.2 / 13.31
// Full loop at:  offsetX ≈ 13.31 / 0.2 ≈ 66.5 mm
//
// 15 stones single row  → step = 66.5 / 15 ≈ 4.43 mm
// 14 stones per row     → step = 66.5 / 14 ≈ 4.75 mm  (double-row, staggered by half-step)

function makeStarfieldEntries(
    gemId: string,
    count: number,
    step: number,
    offsetY: number,
    phaseShift = 0,
): ApiLayoutEntry[] {
    return Array.from({ length: count }, (_, i) => ({
        gemId,
        offsetX: parseFloat((i * step + phaseShift).toFixed(2)),
        offsetY,
    }));
}

/** Single centre row — suited to narrower profiles (4 mm) */
const starfieldSingle: ApiSettingType = {
    id: 'mock-setting-sf-single',
    short: 'SF1',
    styleType: 'flush',
    layoutType: 'STARFIELD',
    orientation: 'radial',
    spacings: [
        {
            id: 'mock-spacing-sf1',
            short: 'STD',
            option: 'DISTRIBUTED',
            distance: 1.5,
            spacingUnit: 'MILLIMETER',
            minProductionDistance: 0.3,
        },
    ],
    cut: brilliantCut,
    allowedGems: [gem1_5],
    layoutDetails: {
        strategy: 'STARFIELD',
        padding: 0.3,
        // 15 stones, single row centred axially (offsetY = 0)
        entries: makeStarfieldEntries('mock-gem-br-1_5', 15, 4.43, 0),
    },
};

/** Two staggered rows — suited to wider profiles (5 mm) */
const starfieldDouble: ApiSettingType = {
    id: 'mock-setting-sf-double',
    short: 'SF2',
    styleType: 'flush',
    layoutType: 'STARFIELD',
    orientation: 'radial',
    spacings: [
        {
            id: 'mock-spacing-sf2',
            short: 'STD',
            option: 'DISTRIBUTED',
            distance: 1.5,
            spacingUnit: 'MILLIMETER',
            minProductionDistance: 0.3,
        },
    ],
    cut: brilliantCut,
    allowedGems: [gem1_5],
    layoutDetails: {
        strategy: 'STARFIELD',
        padding: 0.3,
        // 14 stones × 2 rows at ±1 mm axially, second row offset by half a step
        entries: [
            ...makeStarfieldEntries('mock-gem-br-1_5', 14, 4.75, -1.0),
            ...makeStarfieldEntries('mock-gem-br-1_5', 14, 4.75,  1.0, 2.375),
        ],
    },
};

export const MOCK_RING_TEMPLATES: ApiRingTemplate[] = [
    {
        id: 'mock-template-classic',
        short: 'classic',
        nameDe: 'Classic',
        nameEn: 'Classic',
        descriptionDe: 'Klassisches Trauringprofil',
        descriptionEn: 'Classic wedding band profile',
        sortingIndex: 0,
        gender: 'unisex',
        multipart: false,
        modelFiles: [],
        ringPartials: [
            {
                ...baseRingPartial,
                id: 'mock-partial-classic',
                name: 'Classic',
                fixedWidth: 4,
                ringProfile: makeProfile(
                    'mock-profile-oval',
                    'oval',
                    'Oval',
                    'dxf/Oval.dxf',
                    1.0,
                ),
                alloys: [makeAlloy('mock-alloy-yg-classic', 'YG18', 'Gelbgold 750', yellowGold, 0)],
                gems: [gem1_5],
                settingTypes: [starfieldSingle],
            },
        ],
    },
    {
        id: 'mock-template-modern',
        short: 'modern',
        nameDe: 'Modern',
        nameEn: 'Modern',
        descriptionDe: 'Modernes Trauringprofil',
        descriptionEn: 'Modern wedding band profile',
        sortingIndex: 1,
        gender: 'unisex',
        multipart: false,
        modelFiles: [],
        ringPartials: [
            {
                ...baseRingPartial,
                id: 'mock-partial-modern',
                name: 'Modern',
                fixedWidth: 5,
                ringProfile: makeProfile(
                    'mock-profile-sidecut',
                    'sidecut',
                    'Sidecut',
                    'dxf/Sidecut.dxf',
                    1.0,
                ),
                alloys: [makeAlloy('mock-alloy-wg-modern', 'WG18', 'Weißgold 750', whiteGold, 1)],
                gems: [gem1_5],
                settingTypes: [starfieldDouble],
            },
        ],
    },
];
